import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  Children,
  createContext,
  useContext,
  useId,
  useMemo,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { Div } from '@ui/components/base/div';
import { Label } from '@ui/components/base/label';
import { RadioGroup as RadioGroupRoot, RadioGroupItem } from '@ui/components/base/radio-group';
import { cn } from '@ui/lib/utils';

interface RadioCardGroupContextValue {
  isChecked: <Candidate>(candidate: Candidate) => boolean;
}

const RadioCardGroupContext = createContext<RadioCardGroupContextValue | null>(null);

const RADIO_GROUP_NAVIGATION_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

const keepNavigationKeysInBody = (event: KeyboardEvent<HTMLDivElement>) => {
  if (RADIO_GROUP_NAVIGATION_KEYS.has(event.key)) {
    event.stopPropagation();
  }
};

interface RadioCardGroupProps<T> {
  value: T;
  onValueChange: (value: T) => void;
  children: ReactNode;
  className?: string;
}

const RadioCardGroup = <T,>({ value, onValueChange, children, className }: RadioCardGroupProps<T>) => {
  const contextValue = useMemo<RadioCardGroupContextValue>(
    () => ({ isChecked: (candidate) => Object.is(candidate, value) }),
    [value]
  );
  return (
    <RadioCardGroupContext.Provider value={contextValue}>
      <RadioGroupRoot
        value={value}
        onValueChange={(nextValue) => onValueChange(nextValue as T)}
        className={cn('gap-3 sm:grid-cols-2', className)}
      >
        {children}
      </RadioGroupRoot>
    </RadioCardGroupContext.Provider>
  );
};

interface RadioCardProps<T> {
  value: T;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  badges?: ReactNode;
  disabled?: boolean;
  disabledReason?: ReactNode;
  id?: string;
  className?: string;
  children?: ReactNode;
}

const RadioCard = <T,>({
  value,
  title,
  description,
  icon,
  badges,
  disabled = false,
  disabledReason,
  id,
  className,
  children,
}: RadioCardProps<T>) => {
  const generatedId = useId();
  const cardId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const group = useContext(RadioCardGroupContext);
  const checked = group?.isChecked(value) ?? false;
  const hasBody = checked && !isEmpty(Children.toArray(children));
  const hasIcon = !isNil(icon);
  const text = (
    <>
      <span className="block text-sm-tight font-semibold text-foreground">{title}</span>
      {!isNil(description) && (
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{description}</span>
      )}
      {!isNil(badges) && <span className="mt-1.5 flex flex-wrap gap-1.5">{badges}</span>}
      {disabled && !isNil(disabledReason) && (
        <span className="mt-1.5 block text-xs-plus font-medium text-muted-foreground">{disabledReason}</span>
      )}
    </>
  );

  const selectFromHeaderSpace = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    inputRef.current?.click();
  };

  return (
    <Div
      column
      data-slot="radio-card"
      className={cn(
        'rounded-xl border border-border-strong bg-background transition-colors',
        'has-data-checked:border-primary has-data-checked:bg-accent-soft',
        disabled ? 'opacity-55' : 'hover:border-accent-line',
        className
      )}
    >
      <Div
        row
        data-slot="radio-card-header"
        className="grow items-start gap-2.5 p-3"
        onClick={disabled ? undefined : selectFromHeaderSpace}
      >
        <RadioGroupItem value={value} id={cardId} disabled={disabled} inputRef={inputRef} className="mt-0.5" />
        <Label
          htmlFor={cardId}
          className={cn(
            'min-w-0 flex-1 items-start leading-normal',
            hasIcon ? 'flex-row gap-2.5' : 'flex-col gap-0',
            disabled ? 'cursor-default' : 'cursor-pointer'
          )}
        >
          {hasIcon ? (
            <>
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-fg-muted',
                  "[&_svg:not([class*='size-'])]:size-4.5"
                )}
              >
                {icon}
              </span>
              <span className="flex min-w-0 flex-col">{text}</span>
            </>
          ) : (
            text
          )}
        </Label>
      </Div>
      {hasBody && (
        <Div column data-slot="radio-card-body" className="gap-3 px-3 pb-3" onKeyDown={keepNavigationKeysInBody}>
          {children}
        </Div>
      )}
    </Div>
  );
};

export { RadioCard, RadioCardGroup };
export type { RadioCardGroupProps, RadioCardProps };
